<template>
  <div class="py-4 glass-frosted h-full flex flex-col rounded-8xl gap-4">
    <p class="text-black-white text-l-medium px-4">{{ $t('parameters.sensorAngle.title') }}</p>

    <div class="relative flex-1 flex items-end justify-center px-4 pb-2">
      <!-- 中间 SVG 区 -->
      <svg ref="svgRef" viewBox="0 0 320 356" class="flex-1 max-w-80 max-h-89 relative bottom-2">
        <!-- Layer 1: 最外层圆弧轨道 -->
        <g id="outer-track-layer" class="cursor-move" @mousedown.prevent="onArcPointerDown">
          <path
            :d="outerArcPath"
            fill="none"
            stroke-width="36"
            stroke-linecap="round"
            class="stroke-glass30"
          />
          <circle
            :cx="indicatorPos.x"
            :cy="indicatorPos.y"
            r="15"
            :class="['fill-brand-blue1', { 'transition-all duration-200 ease-out': useTransition }]"
          />
        </g>

        <!-- Layer 2: 刻度点 -->
        <g id="layer-tick-dots" class="cursor-pointer" @click.stop="onArcClick">
          <circle
            :cx="CX"
            :cy="CY"
            :r="MIDDLE_R"
            fill="none"
            stroke="rgba(255, 255, 255, 0.03)"
            stroke-width="1"
            stroke-dasharray="4 4"
          />
          <g v-for="tick in ticks" :key="`tick-${tick}`">
            <circle :cx="getTickPos(tick).x" :cy="getTickPos(tick).y" r="8" fill="transparent" />
            <circle
              :cx="getTickPos(tick).x"
              :cy="getTickPos(tick).y"
              :r="isMajorTick(tick) ? 2.5 : 2"
              :fill="'rgba(255, 255, 255, 0.3)'"
              :stroke="'none'"
              class="transition-all duration-300 ease-out"
            />
          </g>
        </g>

        <!-- Layer 3: 中间无圆角圆弧 + 刻度数字 -->
        <g id="layer-middle-flat-arc" class="cursor-pointer" @click.stop="onArcClick">
          <path
            :d="middleArcPath"
            fill="none"
            stroke-width="44"
            stroke-linecap="butt"
            class="transition-all duration-300 ease-out stroke-neutral-light-default1"
          />
          <path
            :d="middleArcInnerEdgePath"
            fill="none"
            stroke="rgba(255, 255, 255, 0.03)"
            stroke-width="1"
            stroke-linecap="butt"
          />
          <path
            :d="middleArcOuterEdgePath"
            fill="none"
            stroke="rgba(255, 255, 255, 0.03)"
            stroke-width="1"
            stroke-linecap="butt"
          />
          <text
            v-for="lbl in majorLabels"
            :key="`label-${lbl}`"
            :x="getLabelPos(lbl).x"
            :y="getLabelPos(lbl).y"
            text-anchor="middle"
            dominant-baseline="central"
            class="font-[Barlow] text-m-medium select-none transition-all duration-300"
            :fill="getLabelFill(lbl)"
          >
            {{ lbl > 0 ? `+${lbl}°` : `${lbl}°` }}
          </text>
        </g>

        <!-- Layer 4: 最内侧圆盘 + 指针 -->
        <g id="layer-inner-dial" class="cursor-pointer" @click.stop="onArcClick">
          <circle :cx="CX" :cy="CY" :r="INNER_R" class="transition-all duration-300 fill-glass30" />
          <g
            id="pointer-needle-group"
            :transform="`rotate(${pointerRotation}, ${CX}, ${CY})`"
            :class="{ 'transition-transform duration-200 ease-out': useTransition }"
          >
            <line
              :x1="CX"
              :y1="CY"
              :x2="CX"
              :y2="needleEndY"
              stroke-width="5"
              stroke-linecap="round"
              class="stroke-brand-blue1"
            />
          </g>
          <circle :cx="CX" :cy="CY" r="12" class="fill-brand-blue1" />
          <circle :cx="CX" :cy="CY" r="4" opacity="0.8" class="fill-brand-blue1" />
        </g>

        <!-- 底部当前值显示 -->
        <text
          :x="CX + 14.5"
          y="316"
          text-anchor="end"
          dominant-baseline="central"
          class="deco-3xl-bold select-none fill-black-white tabular-nums"
          font-family="Barlow, sans-serif"
        >
          {{ value }}
        </text>
        <text
          :x="CX + 16"
          y="316"
          text-anchor="start"
          dominant-baseline="central"
          class="deco-3xl-bold select-none fill-black-white"
          font-family="Barlow, sans-serif"
        >
          °
        </text>
      </svg>

      <!-- 左控制按钮 -->
      <div class="absolute size-10 left-25.5 bottom-8">
        <Button color="normal" size="icon-md" shape="rect" @click="decrement">
          <Icon icon="Direction-FIll-Left3" class="size-6.5" />
        </Button>
      </div>

      <!-- 右控制按钮 -->
      <div class="absolute size-10 right-26 bottom-8">
        <Button color="normal" size="icon-md" shape="rect" @click="increment">
          <Icon icon="Direction-FIll-Right3" class="size-6.5" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Button, Icon } from '@rapoo/element'
  import { polarToCartesian, sensorAngleToPhysicalAngle, describeArc } from '../utils/guageMath'
  import type { ParametersContext } from '../../composables/useParameters'
  import { useLatestCommit } from '@/composables/useSliderDrag'

  /** SVG 中心坐标 */
  const CX = 160
  const CY = 160
  /** 外圈弧线半径 */
  const OUTER_R = 140
  /** 中间圈半径（刻度点所在） */
  const MIDDLE_R = 105
  /** 角度范围 */
  const MIN_ANGLE = -30
  const MAX_ANGLE = 30
  /** 刻度间隔 */
  const TICK_INTERVAL = 5

  const props = defineProps<{
    parameters: ParametersContext
  }>()
  const ctx = props.parameters
  /** 拖动中的本地预览值 */
  const localValue = ref(0)
  /** 已交给设备、尚未回写完成的角度。连续操作时界面停在最后一次。 */
  const pendingAngle = ref<number | null>(null)
  /** 最外层 layer 是否在拖动 */
  const isDragging = ref(false)
  /** 展示值：拖动中用 localValue，提交中用最后一次，否则用 SDK 状态 */
  const value = computed(() => {
    if (isDragging.value) return localValue.value
    if (pendingAngle.value !== null) return pendingAngle.value
    return ctx.state.sensorAngle.value
  })

  const { commit: commitAngleWrite } = useLatestCommit(async (angle: number) => {
    await ctx.actions.setSensorAngle(angle)
    if (pendingAngle.value === angle) pendingAngle.value = null
  })

  function commitAngle(angle: number) {
    pendingAngle.value = angle
    commitAngleWrite(angle)
  }

  watch(
    () => ctx.state.sensorAngle.value,
    (angle) => {
      if (pendingAngle.value === angle) pendingAngle.value = null
    },
  )

  /** 递减 */
  function decrement() {
    if (value.value > MIN_ANGLE) {
      useTransition.value = true
      commitAngle(value.value - 1)
    }
  }

  /** 递增 */
  function increment() {
    if (value.value < MAX_ANGLE) {
      useTransition.value = true
      commitAngle(value.value + 1)
    }
  }

  /** 外圈弧线起止物理角度 */
  const outerArcStartAngle = sensorAngleToPhysicalAngle(MIN_ANGLE)
  const outerArcEndAngle = sensorAngleToPhysicalAngle(MAX_ANGLE)

  /** 外圈弧线 path */
  const outerArcPath = computed(() => {
    return describeArc(CX, CY, OUTER_R, outerArcStartAngle, outerArcEndAngle)
  })

  /** 蓝色指示圆点位置 */
  const indicatorPos = computed(() => {
    const physicalAngle = sensorAngleToPhysicalAngle(value.value)
    return polarToCartesian(CX, CY, OUTER_R, physicalAngle)
  })

  // ---- Layer 2: 刻度点 ----

  const ticks = computed(() => {
    const result: number[] = []
    for (let val = MIN_ANGLE; val <= MAX_ANGLE; val += TICK_INTERVAL) {
      result.push(val)
    }
    return result
  })

  function getTickPos(tickVal: number) {
    const physicalAngle = sensorAngleToPhysicalAngle(tickVal)
    return polarToCartesian(CX, CY, MIDDLE_R, physicalAngle)
  }

  function isMajorTick(tickVal: number): boolean {
    return tickVal % 15 === 0
  }

  // ---- Layer 3: 中间无圆角圆弧 + 刻度数字 ----

  const MIDDLE_ARC_R = MIDDLE_R - 28

  const middleArcPath = computed(() => {
    return describeArc(CX, CY, MIDDLE_ARC_R, outerArcStartAngle, outerArcEndAngle)
  })

  const middleArcInnerEdgePath = computed(() => {
    return describeArc(CX, CY, MIDDLE_ARC_R - 21, outerArcStartAngle, outerArcEndAngle)
  })

  const middleArcOuterEdgePath = computed(() => {
    return describeArc(CX, CY, MIDDLE_ARC_R + 21, outerArcStartAngle, outerArcEndAngle)
  })

  const majorLabels = [MIN_ANGLE, MIN_ANGLE / 2, 0, MAX_ANGLE / 2, MAX_ANGLE]

  function getLabelPos(lblVal: number) {
    const physicalAngle = sensorAngleToPhysicalAngle(lblVal)
    return polarToCartesian(CX, CY, MIDDLE_ARC_R, physicalAngle)
  }

  function getLabelFill(lblVal: number): string {
    if (value.value === lblVal) {
      return 'var(--color-brand-blue1)'
    }
    return 'var(--color-black-white)'
  }

  // ---- Layer 4: 最内侧圆盘 + 指针 ----

  const INNER_R = MIDDLE_ARC_R - 21 - 8

  const needleLength = INNER_R
  const needleEndY = CY - needleLength

  const pointerRotation = computed(() => {
    const physicalAngle = sensorAngleToPhysicalAngle(value.value)
    return physicalAngle - 270
  })

  // ---- 圆弧拖拽交互 ----
  const svgRef = ref<SVGSVGElement | null>(null)
  const useTransition = ref(false)

  function getAngleFromEvent(e: MouseEvent): number {
    if (!svgRef.value) return localValue.value
    const rect = svgRef.value.getBoundingClientRect()
    const scaleX = 320 / rect.width
    const scaleY = 320 / rect.height
    const svgX = (e.clientX - rect.left) * scaleX
    const svgY = (e.clientY - rect.top) * scaleY

    const dx = svgX - CX
    const dy = svgY - CY
    let angle = Math.atan2(dy, dx) * (180 / Math.PI)
    if (angle < 0) angle += 360

    if (angle < 90) angle += 360

    const startPhysical = outerArcStartAngle
    const endPhysical = outerArcEndAngle
    if (angle < startPhysical) angle = startPhysical
    if (angle > endPhysical) angle = endPhysical

    const sensorAngle = (angle - 270) / 4.5
    return Math.round(Math.max(MIN_ANGLE, Math.min(MAX_ANGLE, sensorAngle)))
  }

  function onArcPointerDown(e: MouseEvent) {
    useTransition.value = false
    isDragging.value = true
    localValue.value = getAngleFromEvent(e)
    document.addEventListener('mousemove', onArcPointerMove)
    document.addEventListener('mouseup', onArcPointerUp)
  }

  function onArcPointerMove(e: MouseEvent) {
    localValue.value = getAngleFromEvent(e)
  }

  function onArcPointerUp() {
    document.removeEventListener('mousemove', onArcPointerMove)
    document.removeEventListener('mouseup', onArcPointerUp)
    isDragging.value = false
    commitAngle(localValue.value)
  }

  function onArcClick(e: MouseEvent) {
    useTransition.value = false
    commitAngle(getAngleFromEvent(e))
  }
</script>

<style lang="scss" scoped></style>
